import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Tab, Tabs } from 'react-toolbox';
import S from 'components/InstallerPrioritizationSliders/styles.scss';

class MeasurePrioritizationSliders extends Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: 0,
    };
  }

  componentDidMount() {
    this.initializeAllCandidateNames();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.candidatesBySurfaceType !== this.props.candidatesBySurfaceType ||
        prevProps.surfaceTypes !== this.props.surfaceTypes) {
      this.initializeAllCandidateNames();
    }
  }

  getCandidatesForSurface = (surfaceType) => {
    const { candidatesBySurfaceType } = this.props;
    return candidatesBySurfaceType[surfaceType] || [];
  };

  getSortedCandidates = (surfaceType) => {
    const list = this.getCandidatesForSurface(surfaceType);
    return [...list].sort((a, b) => (a.phase_count || 0) - (b.phase_count || 0));
  };

  getCandidateWeight = (surfaceType, compositeId) => {
    const { weights } = this.props;
    const surfaceWeights = weights[surfaceType] || {};
    const weightData = surfaceWeights[compositeId];
    if (weightData === null || weightData === undefined) return 0;
    if (typeof weightData === 'object' && weightData.weight !== undefined) {
      return parseInt(weightData.weight, 10) || 0;
    }
    return parseInt(weightData, 10) || 0;
  };

  getUsedPercentage = (surfaceType) => {
    const { weights } = this.props;
    const surfaceWeights = weights[surfaceType] || {};
    return Object.values(surfaceWeights).reduce((sum, val) => {
      if (val === null || val === undefined) return sum;
      if (typeof val === 'object' && val.weight !== undefined) {
        return sum + (parseInt(val.weight, 10) || 0);
      }
      return sum + (parseInt(val, 10) || 0);
    }, 0);
  };

  initializeAllCandidateNames = () => {
    const { weights, surfaceTypes, onChange, disabled } = this.props;
    if (disabled || !surfaceTypes.length) return;

    let hasChanges = false;
    const newWeights = { ...weights };

    surfaceTypes.forEach((surfaceType) => {
      const candidates = this.getCandidatesForSurface(surfaceType);
      if (!candidates.length) return;

      const currentSurfaceWeights = weights[surfaceType] || {};
      const needsInit = candidates.some((c) => {
        const data = currentSurfaceWeights[c.composite_id];
        return !data || typeof data !== 'object' || !data.name;
      });

      if (needsInit) {
        hasChanges = true;
        const newSurfaceWeights = {};
        candidates.forEach((c) => {
          const existingData = currentSurfaceWeights[c.composite_id];
          let weight = 0;
          if (existingData !== null && existingData !== undefined) {
            weight = typeof existingData === 'object' && existingData.weight !== undefined
              ? parseInt(existingData.weight, 10) || 0
              : parseInt(existingData, 10) || 0;
          }
          newSurfaceWeights[c.composite_id] = {
            weight, name: c.name, assignee_type: c.assignee_type,
          };
        });
        newWeights[surfaceType] = newSurfaceWeights;
      }
    });

    if (hasChanges) {
      onChange(newWeights);
    }
  };

  buildSurfaceWeights = (surfaceType, changedCompositeId, newWeight) => {
    const candidates = this.getCandidatesForSurface(surfaceType);
    const newSurfaceWeights = {};

    candidates.forEach((c) => {
      const currentWeight = this.getCandidateWeight(surfaceType, c.composite_id);
      const weight = c.composite_id === changedCompositeId ? newWeight : currentWeight;
      newSurfaceWeights[c.composite_id] = {
        weight, name: c.name, assignee_type: c.assignee_type,
      };
    });

    return newSurfaceWeights;
  };

  handleSliderChange = (surfaceType, compositeId, e) => {
    const value = parseInt(e.target.value, 10);
    const { weights, onChange } = this.props;
    const newSurfaceWeights = this.buildSurfaceWeights(surfaceType, compositeId, value);
    const newWeights = { ...weights, [surfaceType]: newSurfaceWeights };
    onChange(newWeights);
  };

  handleInputChange = (surfaceType, compositeId, e) => {
    const { weights, onChange } = this.props;
    const inputValue = e.target.value;

    let numValue;
    if (inputValue === '' || inputValue === null) {
      numValue = 0;
    } else {
      numValue = parseInt(inputValue, 10);
      if (Number.isNaN(numValue) || numValue < 0 || numValue > 100) return;
    }

    const newSurfaceWeights = this.buildSurfaceWeights(surfaceType, compositeId, numValue);
    const newWeights = { ...weights, [surfaceType]: newSurfaceWeights };
    onChange(newWeights);
  };

  handleTabChange = (index) => {
    this.setState({ activeTab: index });
  };

  renderSurfaceTypeContent = (surfaceType) => {
    const { disabled } = this.props;
    const sortedCandidates = this.getSortedCandidates(surfaceType);
    const usedPercentage = this.getUsedPercentage(surfaceType);

    if (sortedCandidates.length === 0) {
      return (
        <div className={S.tabContent}>
          <div className={S.noInstallersMessage}>
            No candidates available for this surface type.
          </div>
        </div>
      );
    }

    return (
      <div className={S.tabContent}>
        <div className={S.slidersSection}>
          {sortedCandidates.map(candidate => (
            <div key={candidate.composite_id} className={S.sliderRow}>
              <div className={S.installerInfo}>
                <span className={S.installerLabel}>
                  {candidate.assignee_type === 'Estimator' ? 'MT: ' : ''}{candidate.name}
                </span>
                <span className={S.phaseCount}>
                  ({candidate.phase_count} phases)
                </span>
              </div>
              <div className={S.sliderControls}>
                <div className={S.sliderWrapper}>
                  <input
                    type="range"
                    className={S.slider}
                    min={0}
                    max={100}
                    step={5}
                    value={this.getCandidateWeight(surfaceType, candidate.composite_id)}
                    onChange={e =>
                      this.handleSliderChange(surfaceType, candidate.composite_id, e)}
                    disabled={disabled}
                  />
                  <div className={S.sliderRuler}>
                    {[0, 25, 50, 75, 100].map(tick => (
                      <span key={tick} className={S.rulerTick}>{tick}</span>
                    ))}
                  </div>
                </div>
                <div className={S.inputGroup}>
                  <input
                    type="number"
                    className={S.percentInput}
                    min={0}
                    max={100}
                    value={this.getCandidateWeight(surfaceType, candidate.composite_id)}
                    onChange={e =>
                      this.handleInputChange(surfaceType, candidate.composite_id, e)}
                    disabled={disabled}
                  />
                  <span className={S.percentSymbol}>%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className={S.totalRow}>
          <span className={S.totalLabel}>Total:</span>
          <span className={usedPercentage !== 100 ? S.totalValueInvalid : S.totalValue}>
            {usedPercentage}%
          </span>
        </div>
      </div>
    );
  };

  render() {
    const { surfaceTypes } = this.props;
    const { activeTab } = this.state;

    if (!surfaceTypes || surfaceTypes.length === 0) {
      return (
        <div className={S.container}>
          <div className={S.noInstallersMessage}>
            No IFI surface types configured.
          </div>
        </div>
      );
    }

    return (
      <div className={S.container}>
        <Tabs index={activeTab} onChange={this.handleTabChange} className={S.tabs}>
          {surfaceTypes.map(surfaceType => (
            <Tab key={surfaceType} label={surfaceType}>
              {this.renderSurfaceTypeContent(surfaceType)}
            </Tab>
          ))}
        </Tabs>
      </div>
    );
  }
}

MeasurePrioritizationSliders.propTypes = {
  weights: PropTypes.object,
  candidatesBySurfaceType: PropTypes.object,
  surfaceTypes: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

MeasurePrioritizationSliders.defaultProps = {
  weights: {},
  candidatesBySurfaceType: {},
  surfaceTypes: [],
  disabled: false,
};

export default MeasurePrioritizationSliders;



// WEBPACK FOOTER //
// ./client/app/shared/components/MeasurePrioritizationSliders/index.jsx